/**
 * InterestListPage - Admin view of studios who expressed interest in shows
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getInterestSubscriptions,
  deleteInterestSubscription,
  getShows,
} from '../../../api/admin';
import type { InterestSubscription, Show } from '../../../api/admin';

export default function InterestListPage() {
  const [subscriptions, setSubscriptions] = useState<InterestSubscription[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [showFilter, setShowFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  useEffect(() => {
    getShows().then(setShows).catch(() => {});
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [showFilter, page]);

  const loadSubscriptions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getInterestSubscriptions({
        show_id: showFilter || undefined,
        page,
        per_page: perPage,
      });
      setSubscriptions(result.subscriptions);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this subscriber?')) return;
    try {
      await deleteInterestSubscription(id);
      setSubscriptions((prev) => prev.filter((s) => s.id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove subscriber');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalPages = Math.ceil(total / perPage);

  if (isLoading && subscriptions.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/admin/campaigns"
          className="p-2 text-warm-500 hover:text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Interest List</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Studios who want to be notified about upcoming shows.
          </p>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <p className="text-2xl font-bold text-warm-900">{total}</p>
            <p className="text-sm text-warm-600">
              Total active subscribers{showFilter ? ' (filtered)' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:min-w-[200px] sm:w-auto">
            <label className="block text-sm font-medium text-warm-700 mb-1">Filter by Show</label>
            <select
              value={showFilter}
              onChange={(e) => {
                setShowFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Shows</option>
              {shows.map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </select>
          </div>
          {showFilter && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setShowFilter('');
                  setPage(1);
                }}
                className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {subscriptions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No subscribers yet</h3>
          <p className="text-warm-600">
            {showFilter
              ? 'No subscribers found for this show. Try clearing the filter.'
              : 'When studios express interest in shows, they will appear here.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Name</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Show</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Preferred Cities</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Subscribed</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-warm-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-warm-900">{sub.contact_name}</span>
                    </td>
                    <td className="px-6 py-4 text-warm-600">{sub.contact_email}</td>
                    <td className="px-6 py-4 text-warm-700">{sub.show_name || sub.show_id}</td>
                    <td className="px-6 py-4">
                      {(sub.preferred_cities ?? []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(sub.preferred_cities ?? []).map((city) => (
                            <span
                              key={city}
                              className="px-2 py-0.5 rounded-full text-xs bg-warm-100 text-warm-600"
                            >
                              {city}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-warm-400 text-sm">Any</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-warm-500">
                      {formatDate(sub.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleRemove(sub.id)}
                        className="px-3 py-1.5 text-sm text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-white rounded-xl border border-warm-200 p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-warm-900">{sub.contact_name}</p>
                    <p className="text-sm text-warm-600">{sub.contact_email}</p>
                  </div>
                  <button
                    onClick={() => handleRemove(sub.id)}
                    className="px-3 py-1.5 text-sm text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm mb-1">
                  <span className="text-warm-500">Show:</span>
                  <span className="text-warm-700">{sub.show_name || sub.show_id}</span>
                </div>
                {(sub.preferred_cities ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(sub.preferred_cities ?? []).map((city) => (
                      <span
                        key={city}
                        className="px-2 py-0.5 rounded-full text-xs bg-warm-100 text-warm-600"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-warm-400">{formatDate(sub.created_at)}</p>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
              <p className="text-sm text-warm-600">
                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} subscribers
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-warm-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
